import { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FieldRow from 'components/FormElements/FieldRow';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import TranchesMultiSelect from 'components/TrancheMultiSelect';
import GridContainer from 'components/GridContainer';
import downloadFile from 'utils/downloadFile';
import { fundStatusMap } from 'adminConstants/fundProperties';
import { useFunds } from 'hooks/data/fund/useFunds';
import Spacing from 'components/Spacing';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

const useStyles = makeStyles()((theme) => ({
  error: {
    color: theme.palette.error.main,
    padding: '5px 0 0 14px',
  },
  buttonRow: {
    display: 'flex',
    gridGap: '0.3rem',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

const DownloadSheet = ({ callback, skipCallback = null }) => {
  const { classes } = useStyles();
  const [formState, setFormState] = useState<{
    selectedFirms: Array<string>;
    selectedTrancheIds: Array<string>;
    interestPaymentsDate;
  }>({
    selectedFirms: [],
    selectedTrancheIds: [],
    interestPaymentsDate: new Date(),
  });

  const { isPending, mutateAsync: runCreateUploadMutation } =
    api.interestPayment.createUploadDocument.useMutation(
      useHandleTrpcMutation()({}),
    );

  const {
    tranches: { data },
  } = useFunds({ fundStatus: [fundStatusMap.open, fundStatusMap.closed] });

  const tranchesList =
    data
      ?.map((tranche) => {
        return {
          ...tranche,
          label: `${tranche.firmId.firmName} ${tranche.fundName}`,
        };
      })
      ?.sort((item) => item.label?.toLowerCase()) ?? [];

  const handleChangeDate = (date) => {
    setFormState({ ...formState, interestPaymentsDate: date });
  };

  const handleChangeSelectedTranches = (trancheIds) => {
    const selectedFirms = new Set<string>([]);
    trancheIds.forEach((trancheId) => {
      const tranche = data.find((tranche) => tranche._id === trancheId);
      if (tranche) {
        selectedFirms.add(tranche.firmId.firmName);
      }
    });

    setFormState({
      ...formState,
      selectedTrancheIds: trancheIds,
      selectedFirms: Array.from(selectedFirms),
    });
  };

  const handleDownloadButtonClick = async () => {
    const { mimeType, content, name } = await runCreateUploadMutation({
      interestPaymentsDate: formState.interestPaymentsDate,
      selectedTrancheIds: formState.selectedTrancheIds,
    });
    const uint8 = Uint8Array.from(atob(content), (c) => c.charCodeAt(0));
    const blob = new Blob([uint8], { type: mimeType });
    downloadFile(blob, name);
    callback();
  };
  return (
    <Spacing>
      <FieldRow key="1" title="Tranche(s)*">
        <TranchesMultiSelect
          tranches={tranchesList}
          handleChangeSelectedTranches={handleChangeSelectedTranches}
          disabled={false}
        />
        {formState.selectedFirms.length > 1 && (
          <p className={classes.error}>
            Please note: you have selected tranches from two or more firms (
            {formState.selectedFirms.join(', ')}). Please select tranches
            relating to only one firm to continue.
          </p>
        )}
      </FieldRow>
      <FieldRow key="3" title="Date of interest payment*">
        <AppDatePicker
          name="interestPaymentsDate"
          value={formState.interestPaymentsDate}
          onChange={handleChangeDate}
          disableFuture={true}
        />
      </FieldRow>
      <FieldRow key="4">
        {skipCallback && (
          <GridContainer className={classes.buttonRow} spacing={0}>
            <Button variant="text" color="primary" onClick={skipCallback}>
              Skip
            </Button>
            {isPending && <CircularProgress size={18} />}
            <Button
              variant="contained"
              color="primary"
              disabled={
                !formState.selectedTrancheIds.length ||
                !formState.interestPaymentsDate ||
                formState.selectedFirms.length > 1 ||
                isPending
              }
              onClick={() => handleDownloadButtonClick()}
            >
              Create
            </Button>
          </GridContainer>
        )}
      </FieldRow>
    </Spacing>
  );
};

export default DownloadSheet;
